import {SortByDate, SortByNumber, type SortByStrategy, SortByString, SortOrder} from "./sortByStrategy";

export class Sortable {
    private tableElement: HTMLElement;

    public constructor(tableElement: HTMLElement) {
        this.tableElement = tableElement;
    }

    public init(sortableColumns: number[]): void {
        const tablesHeaders = [...this.tableElement.querySelectorAll("th")];
        const buttons = tablesHeaders.map(it => it.querySelector(".table-sort-toggle")!);

        for (const cell of this.tableElement.querySelectorAll("tbody td, thead th")) {
            if (cell.hasAttribute("colspan") || cell.hasAttribute("rowspan")) {
                buttons.forEach(button => button.remove());
                return;
            }
        }

        const rows = [...this.tableElement.querySelectorAll("tbody tr")];

        for (const headerCell of tablesHeaders) {
            const index = tablesHeaders.indexOf(headerCell);
            const button = buttons[index];
            if (!sortableColumns.includes(index + 1)) {
                button.remove();
                continue;
            }
            const cellsToSort = this.getStringsToSort(rows, index);
            const sortStrategy = this.getSortStrategy(cellsToSort);

            button.setAttribute("sortable", "");
            button.addEventListener("click", () => {
                this.removeAttributesFromOtherColumns(buttons, button);
                const sortDirection = this.toggleAndFindSortDirection(button);
                this.sortTable(index, sortDirection, sortStrategy);
            });
        }
    }

    private getStringsToSort(rows: Element[], columnIndex: number): string[] {
        return rows.map(row => {
            const child = row.children[columnIndex];
            return child.children[1]?.textContent?.trim() ?? "";
        });
    }

    public getSortStrategy(cellsToSort: string[]): SortByStrategy {
        let sortByDate: SortByDate | null = new SortByDate();
        let sortByNumber: SortByNumber | null = new SortByNumber();
        const sortByString: SortByString = new SortByString();

        cellsToSort.forEach(cell => {
            if (!sortByDate?.applicableFor(cell)) {
                sortByDate = null;
            }
            if (!sortByNumber?.applicableFor(cell)) {
                sortByNumber = null;
            }
            if (sortByDate === null && sortByNumber === null) {
                return;
            }
        });
        return sortByDate ?? sortByNumber ?? sortByString;
    }

    private removeAttributesFromOtherColumns(buttons: Element[], button: Element): void {
        buttons.forEach(btn => {
            if (btn !== button) {
                btn?.removeAttribute("ascending");
                btn?.removeAttribute("descending");
            }
        });
    }

    private toggleAndFindSortDirection(button: Element): SortOrder {
        const ascending = button.hasAttribute("ascending");
        const descending = button.hasAttribute("descending");

        if (ascending) {
            button.setAttribute("descending", "");
            button.removeAttribute("ascending");
            return SortOrder.DESCENDING;
        } else if (descending) {
            button.setAttribute("ascending", "");
            button.removeAttribute("descending");
            return SortOrder.ASCENDING;
        } else {
            button.setAttribute("ascending", "");
            return SortOrder.ASCENDING;
        }
    }

    private sortTable(columnIndex: number, order: SortOrder, sortStrategy: SortByStrategy): void {
        const body = this.tableElement.querySelector("tbody")!;

        const rows = [...body.children];
        rows.sort((a, b) => {
            const aContent: string = a.children[columnIndex]?.children[1]?.textContent ?? "";
            const bContent: string = b.children[columnIndex]?.children[1]?.textContent ?? "";

            return sortStrategy.sort(aContent, bContent, order);
        });
        rows.forEach(it => body.appendChild(it));
    }
}